import { Code, ConnectError } from '@bufbuild/connect';
import { useEffect } from 'react';

import { DiagnocoinBatch } from 'protocol/api/billing_new/dto_diagnocoin_new_pb';
import { Order } from 'protocol/api/billing_new/dto_order_new_pb';
import { ProductState } from 'protocol/api/billing_new/dto_product_new_pb';

import api from '@/shared/api/api';
import { useAppDispatch } from '@/shared/hooks';

import * as billingModel from '../model';
import { BillingStateLoadersKeys } from '../model/billingSlice';

enum AccountInfoAccumulatorKeys {
  Orders = 'Orders',
  Inventory = 'Inventory',
  DiagnocoinBatches = 'DiagnocoinBatches',
}

type AccountInfoStreamAccumulators = {
  [AccountInfoAccumulatorKeys.Orders]: Order[];
  [AccountInfoAccumulatorKeys.Inventory]: ProductState[];
  [AccountInfoAccumulatorKeys.DiagnocoinBatches]: DiagnocoinBatch[];
};

// The stream reports these in stages, so each widget can tell "still loading"
// and "the connection died" apart from "here is the answer" for its own part.
const STREAM_LOADER_KEYS: BillingStateLoadersKeys[] = [
  'accountInfo',
  'orders',
  'diagnocoinBatches',
];

export const useAccountInfoStream = (organizationID: string) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!organizationID) {
      return undefined;
    }

    const abortController = new AbortController();

    // Scoped to this effect run rather than module-level: a module-level
    // accumulator would keep an aborted organization's unflushed historical
    // items around for the next organization's stream to flush on top of.
    const accumulators: AccountInfoStreamAccumulators = {
      [AccountInfoAccumulatorKeys.Orders]: [],
      [AccountInfoAccumulatorKeys.Inventory]: [],
      [AccountInfoAccumulatorKeys.DiagnocoinBatches]: [],
    };

    const openAccountInfoStream = async () => {
      const accountInfoStream = api.billing.accountInfoStream(
        {
          OrganizationID: organizationID,
        },
        { signal: abortController.signal },
      );

      STREAM_LOADER_KEYS.forEach((loaderKey) => {
        dispatch(
          billingModel.actions.setLoading({ loaderKey, loaderState: 'pending' }),
        );
      });

      try {
        for await (const { Update } of accountInfoStream) {
          switch (Update.case) {
            case 'AccountInfo': {
              dispatch(billingModel.actions.setAccountInfo(Update.value));

              dispatch(
                billingModel.actions.setLoading({
                  loaderKey: 'accountInfo',
                  loaderState: 'succeeded',
                }),
              );
              break;
            }
            case 'Order': {
              dispatch(billingModel.actions.updateOrder(Update.value));
              break;
            }

            case 'DiagnocoinWallet': {
              dispatch(billingModel.actions.setDiagnocoinWallet(Update.value));
              break;
            }

            case 'DiagnocoinBatch': {
              dispatch(billingModel.actions.updateDiagnocoinBatch(Update.value));
              break;
            }

            case 'HistoricalDiagnocoinBatch': {
              accumulators[AccountInfoAccumulatorKeys.DiagnocoinBatches].push(
                Update.value,
              );
              break;
            }

            case 'EndOfHistoricalDiagnocoinBatch': {
              dispatch(
                billingModel.actions.setDiagnocoinBatches(
                  accumulators[AccountInfoAccumulatorKeys.DiagnocoinBatches],
                ),
              );

              accumulators[AccountInfoAccumulatorKeys.DiagnocoinBatches] = [];

              dispatch(
                billingModel.actions.setLoading({
                  loaderKey: 'diagnocoinBatches',
                  loaderState: 'succeeded',
                }),
              );
              break;
            }

            case 'HistoricalOrder': {
              accumulators[AccountInfoAccumulatorKeys.Orders].push(Update.value);
              break;
            }

            case 'Inventory': {
              dispatch(billingModel.actions.updateInventory(Update.value));
              break;
            }

            case 'HistoricalInventory': {
              accumulators[AccountInfoAccumulatorKeys.Inventory].push(
                Update.value,
              );
              break;
            }

            case 'EndOfHistoricalOrder': {
              dispatch(
                billingModel.actions.setOrders(
                  accumulators[AccountInfoAccumulatorKeys.Orders],
                ),
              );

              accumulators[AccountInfoAccumulatorKeys.Orders] = [];

              dispatch(
                billingModel.actions.setLoading({
                  loaderKey: 'orders',
                  loaderState: 'succeeded',
                }),
              );
              break;
            }
            case 'EndOfHistoricalInventory': {
              dispatch(
                billingModel.actions.setInventory(
                  accumulators[AccountInfoAccumulatorKeys.Inventory],
                ),
              );

              accumulators[AccountInfoAccumulatorKeys.Inventory] = [];
              break;
            }
            default: {
              // do nothing
            }
          }
        }
      } catch (error) {
        if (error instanceof ConnectError && error.code !== Code.Canceled) {
          console.error(
            'AccountInfoStream:: error: ',
            error.message,
            error.code,
            error,
          );

          STREAM_LOADER_KEYS.forEach((loaderKey) => {
            dispatch(
              billingModel.actions.setLoading({ loaderKey, loaderState: 'failed' }),
            );
          });
        }
      }
    };

    openAccountInfoStream();

    return () => {
      abortController.abort();

      dispatch(billingModel.actions.reset());
    };
  }, [dispatch, organizationID]);
};
